import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Eye } from 'lucide-react';

export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="nosotros" 
      className="py-24 px-4 sm:px-6 lg:px-8 bg-zinc-950"
      ref={ref}
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="relative rounded-lg overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1519861531473-9200262188bf?q=80&w=2071"
                alt="Equipo Jotuns Basketball Club"
                className="w-full h-[500px] object-cover"
                data-testid="about-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="text-orange-500 font-bold tracking-widest text-sm">DESDE 2019</span>
                <h3 className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  FORMANDO CAMPEONES
                </h3>
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-orange-500/30 rounded-lg -z-10" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="text-orange-500 font-semibold tracking-widest text-sm">SOBRE NOSOTROS</span>
            <h2 
              className="text-4xl sm:text-5xl font-extrabold text-white mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed' }}
              data-testid="about-title"
            >
              JOTUNS BASKETBALL CLUB
            </h2>
            
            <p className="text-zinc-400 text-lg mb-6 leading-relaxed" data-testid="about-description">
              Somos una organización deportiva juvenil ubicada en <strong className="text-white">Sesquilé, Cundinamarca</strong>, 
              comprometida con la formación integral de niños, niñas y jóvenes a través del baloncesto. 
              Como parte de la <strong className="text-orange-500">Plataforma Municipal de Juventud</strong>, 
              trabajamos en articulación con las políticas públicas de juventud del municipio.
            </p>
            
            <p className="text-zinc-400 mb-8 leading-relaxed">
              Nuestro club no solo entrena deportistas, sino que forma líderes comprometidos con su comunidad, 
              promoviendo valores como la disciplina, el trabajo en equipo, la inclusión y el respeto.
            </p>

            {/* Mission & Vision */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bento-item p-6 rounded-lg" data-testid="about-mission">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Target className="text-orange-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>MISIÓN</h3>
                <p className="text-zinc-400 text-sm">
                  Fomentar el desarrollo integral de la juventud sesquileña a través del baloncesto, 
                  promoviendo valores y habilidades para la vida.
                </p>
              </div>
              
              <div className="bento-item p-6 rounded-lg" data-testid="about-vision">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                  <Eye className="text-amber-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>VISIÓN</h3>
                <p className="text-zinc-400 text-sm">
                  Ser referentes regionales en la formación deportiva juvenil, reconocidos por nuestro 
                  impacto social y la excelencia deportiva.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
